import React, { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, Download, FileText } from "lucide-react";
import * as XLSX from "xlsx"; 
import Mark from "mark.js"; 
import { renderAsync } from "docx-preview"; 

function getExt(url = "") {
  try {
    const u = new URL(url);
    const path = u.pathname.toLowerCase();
    const m = path.match(/\.([a-z0-9]+)$/);
    return m?.[1] || "";
  } catch {
    const clean = url.split("#")[0].split("?")[0].toLowerCase();
    const m = clean.match(/\.([a-z0-9]+)$/);
    return m?.[1] || "";
  }
}

function buildPdfUrl(rawUrl, { page, highlightText }) {
  const pageNum = page ? String(page).match(/\d+/)?.[0] : null;
  const trimmed = highlightText?.trim() || "";
  const hasValidHighlight = trimmed.length > 0 && trimmed.length <= 300;

  // Combine page navigation with a text-fragment highlight. The browser strips
  // the `:~:text=` directive from the fragment before the PDF viewer sees it, so
  // the viewer still reads `page=N` and lands on the right page, while the
  // text fragment highlights the snippet when it matches the PDF text. If the
  // text doesn't match, the page navigation still works (no stranding on page 1).
  if (pageNum && hasValidHighlight) {
    return `${rawUrl}#page=${pageNum}:~:text=${encodeURIComponent(trimmed)}`;
  }
  if (pageNum) {
    return `${rawUrl}#page=${pageNum}`;
  }
  if (hasValidHighlight) {
    return `${rawUrl}#:~:text=${encodeURIComponent(trimmed)}`;
  }
  return rawUrl;
}

// --- UPDATED: DOCX Viewer using docx-preview for High Fidelity ---
function DocxViewer({ url, highlightText }) {
  const [isRendered, setIsRendered] = useState(false);
  const [err, setErr] = useState("");
  const containerRef = React.useRef(null);

  useEffect(() => {
    if (!url || !containerRef.current) return;
    let alive = true;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob(); // docx-preview prefers a blob
      })
      .then((blob) => {
        if (!alive) return;
        // docx-preview renders directly into the container Ref
        return renderAsync(blob, containerRef.current, null, {
          className: "docx", // default class name
          inWrapper: true, // wraps pages in a visual A4 container
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true, // Re-creates actual word pages
          experimental: false,
        });
      })
      .then(() => {
        if (!alive) return;
        setIsRendered(true); // Signal that DOM is ready for Mark.js
        setErr("");
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e.message || "Failed to load DOCX");
      });

    return () => {
      alive = false;
    };
  }, [url]);

  useEffect(() => {
    const cleanHighlight = highlightText?.trim();
    if (!isRendered || !cleanHighlight || !containerRef.current) return;

    const markInstance = new Mark(containerRef.current);
    markInstance.mark(cleanHighlight, {
      element: "mark",
      className: "bg-yellow-300 rounded-sm px-0.5 text-gray-900 shadow-sm",
      accuracy: "partially",
      separateWordSearch: false,
      acrossElements: true, // Needed because text spans across layout divs
      done: (totalMatches) => {
        if (totalMatches > 0) {
          setTimeout(() => {
            const firstMark = containerRef.current.querySelector("mark");
            if (firstMark) {
              firstMark.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 200);
        }
      }
    });

    return () => markInstance.unmark();
  }, [isRendered, highlightText]);

  if (err) return <div className="p-4 text-sm text-red-600 font-medium">DOCX error: {err}</div>;
  
  return (
    <div className="h-full overflow-auto bg-gray-200 p-4 relative">
      {!isRendered && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <span className="text-sm text-gray-500 animate-pulse">Loading Document Layout…</span>
        </div>
      )}
      {/* Container where docx-preview mounts the pages. Styles added to simulate Word Viewer background */}
      <div 
        ref={containerRef} 
        className="mx-auto" 
        style={{ width: "fit-content" }}
      />
    </div>
  );
}

// Excel renderer with highlighting and auto-scroll
function ExcelViewer({ url, highlightText }) {
  const [html, setHtml] = useState("");
  const [err, setErr] = useState("");
  const containerRef = React.useRef(null);
  
  useEffect(() => {
    if (!url) return;
    let alive = true;
  
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then((buffer) => {
        if (!alive) return;
        const wb = XLSX.read(buffer, { type: "array" });
        const firstSheet = wb.Sheets[wb.SheetNames[0]];
        const htmlStr = XLSX.utils.sheet_to_html(firstSheet, { header: "", footer: "" });
          
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlStr, "text/html");
        const table = doc.querySelector("table");
          
        if (table) {
          table.className = "w-full text-left border-collapse text-sm font-outfit";
            
          const rows = table.querySelectorAll("tr");
          rows.forEach((tr, index) => {
            tr.className = "border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150 ease-in-out group";
            if (index === 0) tr.classList.add("bg-gray-50", "font-medium", "text-gray-900");
          });
  
          const cells = table.querySelectorAll("td, th");
          cells.forEach((td) => {
            td.className = "px-4 py-3 border-r border-gray-200 last:border-r-0 text-gray-700 whitespace-pre-wrap break-words align-top leading-relaxed tracking-wide";
            if (!td.textContent.trim()) td.innerHTML = "&nbsp;";
          });
  
          setHtml(table.outerHTML);
        } else {
          setHtml(htmlStr);
        }
        setErr("");
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e.message || "Failed to load Excel");
      });
  
    return () => {
      alive = false;
    };
  }, [url]);
  
  useEffect(() => {
    if (!html || !highlightText || !containerRef.current) return;
  
    const markInstance = new Mark(containerRef.current);
    markInstance.mark(highlightText, {
      element: "mark",
      className: "bg-yellow-300 rounded-sm px-0.5 text-gray-900",
      accuracy: "partially",
      separateWordSearch: false,
      done: (totalMatches) => {
        if (totalMatches > 0) {
          setTimeout(() => {
            const firstMark = containerRef.current.querySelector("mark");
            if (firstMark) {
              firstMark.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 100);
        }
      }
    });
  
    return () => markInstance.unmark();
  }, [html, highlightText]);
  
  if (err) return <div className="p-4 text-sm text-red-600 font-medium">Excel error: {err}</div>;
  if (!html) return <div className="p-4 text-sm text-gray-500 animate-pulse">Loading Excel…</div>;
  
  return (
    <div
      ref={containerRef}
      className="p-6 h-full overflow-auto bg-white"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
  
// CSV renderer with highlighting
function CsvViewer({ url, highlightText }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const containerRef = React.useRef(null);

  useEffect(() => {
    if (!url) return;
    let alive = true;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => {
        if (!alive) return;
        setText(t);
        setErr("");
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e.message || "Failed to load CSV");
      });

    return () => {
      alive = false;
    };
  }, [url]);

  useEffect(() => {
    if (!text || !highlightText || !containerRef.current) return;

    const markInstance = new Mark(containerRef.current);
    markInstance.mark(highlightText, {
      element: "mark",
      className: "bg-yellow-300",
    });

    return () => markInstance.unmark();
  }, [text, highlightText]);

  if (err) return <div className="p-4 text-sm text-red-600">CSV error: {err}</div>;
  if (!text) return <div className="p-4 text-sm text-gray-500">Loading CSV…</div>;

  return (
    <div ref={containerRef} className="p-3 h-full overflow-auto bg-white">
      <pre className="text-xs whitespace-pre-wrap break-words text-gray-800">{text}</pre>
    </div>
  );
}

function buildViewer({ url, ext, page, highlightText }) {
  if (!url) return { kind: "none", src: "" };

  if (ext === "pdf") {
    return { kind: "iframe", src: buildPdfUrl(url, { page, highlightText }) };
  }

  if (["xls", "xlsx"].includes(ext)) {
    return { kind: "excel", src: url };
  }

  if (ext === "csv") {
    return { kind: "csv", src: url };
  }

  if (ext === "docx") {
    return { kind: "docx", src: url };
  }

  // Fallback for doc/ppt
  if (["doc", "ppt", "pptx"].includes(ext)) {
    const officeSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
      url
    )}`;
    return { kind: "iframe", src: officeSrc };
  }

  return { kind: "iframe", src: url };
}

const SourceViewModal = ({ isOpen, onClose, data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [canMountViewer, setCanMountViewer] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
      const mountTimer = setTimeout(() => setCanMountViewer(true), 350);
      return () => clearTimeout(mountTimer);
    } else {
      setCanMountViewer(false);
      const timer = setTimeout(() => setIsVisible(false), 300);
      document.body.style.overflow = "unset";
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const rawUrl = data?.document_url || data?.url || "";
  const ext = useMemo(() => getExt(rawUrl), [rawUrl]);
  const highlightText = data?.source_text || data?.highlightText || "";
  const pageNum = data?.page_number || data?.page || "";
  const docName = data?.document_name || data?.name || "Document Viewer";

  const viewer = useMemo(
    () =>
      buildViewer({
        url: rawUrl,
        ext,
        page: pageNum,
        highlightText: highlightText,
      }),
    [rawUrl, ext, pageNum, highlightText]
  );

  if (!isVisible && !isOpen) return null;

  const showHighlightCard = highlightText && !["pdf", "xlsx", "xls", "csv", "docx"].includes(ext);

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end font-outfit">
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ease-in-out ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      <div
        className={`relative w-full lg:w-[55%] h-full bg-white shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shadow-sm z-20 relative">
          <div className="flex items-center gap-3 overflow-hidden">
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex flex-col overflow-hidden">
              <h3 className="text-sm font-semibold text-gray-900 truncate" title={docName}>
                {docName}
              </h3>
              <span className="text-xs text-gray-500">
                {ext ? ext.toUpperCase() : "FILE"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {rawUrl && (
              <a
                href={rawUrl}
                target="_blank"
                rel="noreferrer"
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                title="Download"
              >
                <Download className="w-5 h-5" />
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 bg-gray-100 relative overflow-hidden flex flex-col">
          {showHighlightCard && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-3 z-20 relative">
              <FileText className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-amber-900 mb-1">Referenced Text</p>
                <p className="text-sm text-amber-800 leading-relaxed">"{highlightText}"</p>
              </div>
            </div>
          )}

          <div className="flex-1 relative w-full h-full">
            {!rawUrl ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                No document URL provided
              </div>
            ) : !canMountViewer ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-500 animate-pulse">
                Loading document…
              </div>
            ) : viewer.kind === "excel" ? (
              <ExcelViewer url={viewer.src} highlightText={highlightText} />
            ) : viewer.kind === "csv" ? (
              <CsvViewer url={viewer.src} highlightText={highlightText} />
            ) : viewer.kind === "docx" ? (
              <DocxViewer url={viewer.src} highlightText={highlightText} />
            ) : (
              <iframe key={viewer.src} src={viewer.src} className="w-full h-full border-none bg-white" title="Document Viewer" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourceViewModal;
